import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { RECORDS_PAGE_SIZE } from "../features/salesDataImport/constants";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { useSalesImportBranchFilter } from "../features/salesDataImport/hooks/useSalesImportBranchFilter";
import { deleteSalesImportLineChunksForWeek } from "../features/salesDataImport/services/salesLineChunksService";
import {
  aggregateVoucherTypeSalesFromRows,
  countAllLineChunkRows,
  deleteAllSalesImportLineChunks,
  listUploadedWeekPeriods,
  loadWeeklyLineChunkRows,
} from "../features/salesDataImport/services/salesLineChunksReader";
import { rowMatchesDateRange } from "../features/salesDataImport/utils/dateParse";
import { exportRecordsExcel } from "../features/salesDataImport/services/uploadErrorsService";
import { formatWeekRangeLabel } from "../features/salesDataImport/utils/weekUtils";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";

export default function SalesDataImportRecordsPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const { filterRows, branchFilterLabel, branchId } = useSalesImportBranchFilter();

  const [uploadedWeeks, setUploadedWeeks] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");

  const [weekCount, setWeekCount] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState("");
  const [salesFromDate, setSalesFromDate] = useState("");
  const [salesToDate, setSalesToDate] = useState("");
  const [salesVoucherType, setSalesVoucherType] = useState("all");
  const [salesSummaryRows, setSalesSummaryRows] = useState([]);
  const [salesSummaryTotals, setSalesSummaryTotals] = useState({ records: 0, saleAmount: 0 });
  const [voucherAnalysisSource, setVoucherAnalysisSource] = useState("");
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesProgress, setSalesProgress] = useState(0);

  const selectedWeekMeta = useMemo(
    () => uploadedWeeks.find((w) => w.periodKey === selectedPeriodKey) || null,
    [uploadedWeeks, selectedPeriodKey]
  );

  const branchScopedRows = useMemo(() => filterRows(allRows), [allRows, branchId, filterRows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return branchScopedRows;
    return branchScopedRows.filter(
      (r) =>
        String(r.billNo || "").toLowerCase().includes(term) ||
        String(r.customerName || "").toLowerCase().includes(term) ||
        String(r.partNo || "").toLowerCase().includes(term) ||
        String(r.itemName || "").toLowerCase().includes(term)
    );
  }, [branchScopedRows, search]);

  const items = useMemo(() => {
    const start = tablePage * RECORDS_PAGE_SIZE;
    return filteredRows.slice(start, start + RECORDS_PAGE_SIZE);
  }, [filteredRows, tablePage]);

  const hasMoreTable = (tablePage + 1) * RECORDS_PAGE_SIZE < filteredRows.length;

  const loadCounts = async () => {
    try {
      const total = await countAllLineChunkRows();
      setTotalCount(total);
      if (selectedWeekMeta) {
        setWeekCount(selectedWeekMeta.rowCount);
      } else {
        setWeekCount(null);
      }
    } catch {
      setTotalCount(null);
      setWeekCount(null);
    }
  };

  const refreshWeekList = async () => {
    const weeks = await listUploadedWeekPeriods();
    setUploadedWeeks(weeks);
    if (weeks.length && !weeks.some((w) => w.periodKey === selectedPeriodKey)) {
      setSelectedPeriodKey(weeks[0].periodKey);
    }
    return weeks;
  };

  const loadWeekRows = async (meta) => {
    if (!meta) {
      setAllRows([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await loadWeeklyLineChunkRows(meta.year, meta.monthIndex, meta.weekOfMonth);
      setAllRows(rows);
      setTablePage(0);
      setWeekCount(rows.length);
      setSalesFromDate("");
      setSalesToDate("");
    } catch (e) {
      toast.error(e?.message || "Failed to load line chunks.");
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    refreshWeekList().then(() => loadCounts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    loadWeekRows(selectedWeekMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekMeta?.periodKey]);

  useEffect(() => {
    setTablePage(0);
  }, [search, branchId]);

  useEffect(() => {
    if (!branchScopedRows.length || !selectedWeekMeta) {
      setSalesSummaryRows([]);
      setSalesSummaryTotals({ records: 0, saleAmount: 0 });
      setVoucherAnalysisSource("");
      return;
    }
    const hasDateFilter = Boolean(salesFromDate || salesToDate);
    const sourceRows = hasDateFilter
      ? branchScopedRows.filter((r) => rowMatchesDateRange(r, salesFromDate, salesToDate))
      : branchScopedRows;
    const out = aggregateVoucherTypeSalesFromRows(sourceRows, salesVoucherType);
    setSalesSummaryRows(out.rows);
    setSalesSummaryTotals(out.totals);
    let label = `${sourceRows.length.toLocaleString()} lines from loaded week ${selectedWeekMeta.periodKey}`;
    if (hasDateFilter) {
      label += ` (TranDate ${salesFromDate || "…"} → ${salesToDate || "…"})`;
    }
    setVoucherAnalysisSource(label);
    setVoucherOptions(out.voucherTypeOptions || []);
  }, [branchScopedRows, selectedWeekMeta, salesFromDate, salesToDate, salesVoucherType]);

  const refreshAll = async () => {
    await refreshWeekList();
    await loadCounts();
    await loadWeekRows(selectedWeekMeta);
  };

  const runVoucherSalesAnalysis = async () => {
    if (salesFromDate && salesToDate && salesFromDate > salesToDate) {
      toast.error("From date should be before To date.");
      return;
    }
    setSalesLoading(true);
    setSalesProgress(0);
    try {
      let sourceRows = [];
      let sourceLabel = "";

      if (branchScopedRows.length > 0 && selectedWeekMeta) {
        const hasDateFilter = Boolean(salesFromDate || salesToDate);
        sourceRows = hasDateFilter
          ? branchScopedRows.filter((r) => rowMatchesDateRange(r, salesFromDate, salesToDate))
          : branchScopedRows;
        sourceLabel = `${sourceRows.length.toLocaleString()} lines from loaded week ${selectedWeekMeta.periodKey}`;
        if (hasDateFilter) {
          sourceLabel += ` (TranDate ${salesFromDate || "…"} → ${salesToDate || "…"})`;
        }
      } else if (salesFromDate || salesToDate) {
        const { loadLineChunkRowsForDateRange } = await import(
          "../features/salesDataImport/services/salesLineChunksReader"
        );
        const loaded = await loadLineChunkRowsForDateRange(
          { fromDate: salesFromDate, toDate: salesToDate },
          (n) => setSalesProgress(n)
        );
        sourceRows = loaded.rows;
        sourceLabel = `${sourceRows.length.toLocaleString()} lines from lineChunks (${salesFromDate || "…"} → ${salesToDate || "…"})`;
      } else {
        toast.error("Select an uploaded week above, or set From/To dates.");
        return;
      }

      const out = aggregateVoucherTypeSalesFromRows(sourceRows, salesVoucherType);
      setSalesSummaryRows(out.rows);
      setSalesSummaryTotals(out.totals);
      setVoucherAnalysisSource(sourceLabel);
      setVoucherOptions(out.voucherTypeOptions || []);
      if (!out.rows.length) {
        toast("No matching lines for selected voucher type / dates.");
      } else if (out.totals.records !== sourceRows.length) {
        toast(
          `Analyzed ${out.totals.records.toLocaleString()} of ${sourceRows.length.toLocaleString()} lines (voucher filter).`,
          { duration: 5000 }
        );
      }
    } catch (e) {
      toast.error(e?.message || "Failed to run voucher sales analysis.");
    } finally {
      setSalesLoading(false);
    }
  };

  const handleDeleteWeek = async () => {
    if (!selectedWeekMeta) {
      toast.error("Select a week first.");
      return;
    }
    const rangeText = formatWeekRangeLabel(selectedWeekMeta.fromDate, selectedWeekMeta.toDate);
    const count = weekCount ?? selectedWeekMeta.rowCount;

    const ok = window.confirm(
      `Delete ${count.toLocaleString()} line(s) for ${selectedWeekMeta.periodKey}?\n\n` +
        `Week dates: ${rangeText}\n\n` +
        `This removes lineChunks for this week only. Cannot be undone.`
    );
    if (!ok) return;

    setDeleting(true);
    setDeleteProgress("Deleting…");
    try {
      await deleteSalesImportLineChunksForWeek(
        selectedWeekMeta.year,
        selectedWeekMeta.monthIndex,
        selectedWeekMeta.weekOfMonth
      );
      toast.success(`Deleted week ${selectedWeekMeta.periodKey}.`);
      setAllRows([]);
      await refreshAll();
    } catch (e) {
      toast.error(formatFirebaseError(e), { duration: 8000 });
    } finally {
      setDeleting(false);
      setDeleteProgress("");
    }
  };

  const handleDeleteAll = async () => {
    const count = totalCount ?? (await countAllLineChunkRows());
    const typed = window.prompt(
      `Delete ALL ${count.toLocaleString()} imported lines (all weekly lineChunks)?\n\n` +
        `Type DELETE to confirm. This cannot be undone.`
    );
    if (typed !== "DELETE") {
      if (typed != null) toast.error("Cancelled — type DELETE exactly to confirm.");
      return;
    }

    setDeleting(true);
    setDeleteProgress("Deleted 0…");
    try {
      await deleteAllSalesImportLineChunks((done) => {
        setDeleteProgress(`Removed ${done.toLocaleString()} chunk doc(s)…`);
      });
      toast.success("All weekly lineChunks deleted.");
      setAllRows([]);
      setSelectedPeriodKey("");
      await refreshAll();
    } catch (e) {
      toast.error(formatFirebaseError(e), { duration: 8000 });
    } finally {
      setDeleting(false);
      setDeleteProgress("");
    }
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Imported Sales Records</h1>
          <p>View weekly lineChunks by week date (fast for 30k+ lines)</p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>
      <div className="sdic-body">
        <SalesImportNav active="records" />
        <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)", margin: "-0.35rem 0 0.75rem" }}>
          Branch filter: <strong>{branchFilterLabel}</strong>
        </p>

        <section className="sdic-panel" style={{ borderColor: "var(--sdic-danger)" }}>
          <h2 className="sdic-panel-title">Delete Records</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", marginTop: 0 }}>
            Total lines (all weeks):{" "}
            <strong>{totalCount == null ? "…" : totalCount.toLocaleString()}</strong>
            {deleteProgress && ` · ${deleteProgress}`}
          </p>

          <div className="sdic-week-banner">
            <label>
              Week to delete
              <select
                value={selectedPeriodKey}
                onChange={(e) => setSelectedPeriodKey(e.target.value)}
                disabled={deleting}
                style={{ padding: "0.4rem", minWidth: "220px" }}
              >
                <option value="">— Select week —</option>
                {uploadedWeeks.map((w) => (
                  <option key={w.periodKey} value={w.periodKey}>
                    {w.periodKey} ({w.fromDate} → {w.toDate}) · {w.rowCount.toLocaleString()} lines
                  </option>
                ))}
              </select>
            </label>
            <span style={{ fontSize: "0.85rem", alignSelf: "center" }}>
              {selectedWeekMeta && weekCount != null
                ? `${weekCount.toLocaleString()} line(s) loaded for this week`
                : ""}
            </span>
          </div>

          <div className="sdic-toolbar">
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              style={{ borderColor: "var(--sdic-danger)", color: "var(--sdic-danger)" }}
              disabled={deleting || !selectedWeekMeta}
              onClick={handleDeleteWeek}
            >
              Delete selected week
            </button>
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              style={{ borderColor: "var(--sdic-danger)", color: "var(--sdic-danger)" }}
              disabled={deleting}
              onClick={handleDeleteAll}
            >
              Delete ALL records
            </button>
            <Link to="/sales-data-import/history" className="sdic-btn sdic-btn-secondary">
              Or delete one upload from History
            </Link>
          </div>
        </section>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Browse week lineChunks</h2>
          <div className="sdic-week-banner">
            <label>
              Uploaded week
              <select
                value={selectedPeriodKey}
                onChange={(e) => setSelectedPeriodKey(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "280px" }}
              >
                <option value="">— Select uploaded week —</option>
                {uploadedWeeks.map((w) => (
                  <option key={w.periodKey} value={w.periodKey}>
                    {w.periodKey} · {w.fromDate} → {w.toDate} · {w.rowCount.toLocaleString()} lines
                  </option>
                ))}
              </select>
            </label>
          </div>
          {selectedWeekMeta && (
            <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", margin: "0.5rem 0 0" }}>
              Week dates: <strong>{selectedWeekMeta.fromDate}</strong> → <strong>{selectedWeekMeta.toDate}</strong>
              {weekCount != null && (
                <>
                  {" "}
                  · <strong>{weekCount.toLocaleString()}</strong> line(s) loaded
                </>
              )}
            </p>
          )}
        </section>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Voucher Type Based Sale Amount</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", marginTop: 0 }}>
            Uses every line in the <strong>loaded week</strong> ({weekCount?.toLocaleString() || "0"} rows). Amount =
            sum of Excel <strong>Amount</strong> per line. Optional From/To dates filter by <strong>TranDate</strong> only
            (leave blank to include all lines in the upload).
          </p>
          <div className="sdic-week-banner">
            <label>
              From Date
              <input type="date" value={salesFromDate} onChange={(e) => setSalesFromDate(e.target.value)} />
            </label>
            <label>
              To Date
              <input type="date" value={salesToDate} onChange={(e) => setSalesToDate(e.target.value)} />
            </label>
            <label>
              Voucher Type
              <select
                value={salesVoucherType}
                onChange={(e) => setSalesVoucherType(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "180px" }}
              >
                <option value="all">All Voucher Types</option>
                {voucherOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              disabled={salesLoading}
              onClick={runVoucherSalesAnalysis}
            >
              {salesLoading ? "Analyzing…" : "Show Sale Amount"}
            </button>
          </div>
          <p style={{ fontSize: "0.8rem", color: "var(--sdic-muted)", margin: "0 0 0.6rem" }}>
            {salesLoading
              ? `Analyzing… ${salesProgress.toLocaleString()} rows`
              : voucherAnalysisSource
                ? `${voucherAnalysisSource} · Records: ${salesSummaryTotals.records.toLocaleString()} · Total Amount: ₹ ${salesSummaryTotals.saleAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                : "Select a week, then click Show Sale Amount."}
          </p>
          <div className="sdic-table-wrap">
            <table className="sdic-table">
              <thead>
                <tr>
                  <th>Voucher Type</th>
                  <th>Records</th>
                  <th>Sale Amount</th>
                </tr>
              </thead>
              <tbody>
                {salesSummaryRows.map((r) => (
                  <tr key={r.voucherType}>
                    <td>{r.voucherType}</td>
                    <td>{r.records.toLocaleString()}</td>
                    <td>₹ {r.saleAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {!salesSummaryRows.length && (
                  <tr>
                    <td colSpan={3} style={{ color: "var(--sdic-muted)" }}>
                      Run analysis to view voucher-type sale amount.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="sdic-toolbar">
          <input
            className="sdic-search"
            placeholder="Search BillNo, Customer, PartNo, ItemName…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="sdic-btn sdic-btn-primary"
            onClick={() => exportRecordsExcel(filteredRows, "sales-linechunks-export.xlsx")}
            disabled={!filteredRows.length}
          >
            Export week to Excel
          </button>
          <button type="button" className="sdic-btn sdic-btn-secondary" onClick={refreshAll} disabled={loading}>
            Refresh
          </button>
        </div>

        {loading && !allRows.length ? (
          <p>Loading lineChunks…</p>
        ) : (
          <div className="sdic-table-wrap">
            <table className="sdic-table">
              <thead>
                <tr>
                  <th>BillNo</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>PartNo</th>
                  <th>ItemName</th>
                  <th>ProductID</th>
                  <th>Week</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <td>{r.billNo}</td>
                    <td>{r.tranDate}</td>
                    <td>{r.customerName}</td>
                    <td>{r.partNo}</td>
                    <td>{r.itemName || "—"}</td>
                    <td>{r.productId}</td>
                    <td>{r.periodKey || selectedPeriodKey || "—"}</td>
                    <td>{r.qty}</td>
                    <td>{r.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && selectedPeriodKey && filteredRows.length === 0 && (
          <p style={{ color: "var(--sdic-muted)" }}>
            {branchId !== "all" && allRows.length > 0
              ? `No lines for ${branchFilterLabel} in this week. Try All branches, or re-upload with BranchID column.`
              : "No matching lines in this week."}
          </p>
        )}
        {!selectedPeriodKey && !loading && (
          <p style={{ color: "var(--sdic-muted)" }}>Select an uploaded week to load lineChunks.</p>
        )}
        {hasMoreTable && (
          <button
            type="button"
            className="sdic-btn sdic-btn-secondary"
            style={{ marginTop: "1rem" }}
            onClick={() => setTablePage((p) => p + 1)}
            disabled={loading}
          >
            Show more ({items.length} / {filteredRows.length.toLocaleString()})
          </button>
        )}
      </div>
    </div>
  );
}
